<script setup lang="ts">
    import {computed, reactive} from 'vue';
    import ChatCheckmarkIcon from './ChatCheckmarkIcon.vue';
    import type {ReturnLine} from './chatApi';

    const props = defineProps<{
        eligibility: Record<string, any>;
        disabled: boolean;
    }>();

    const emit = defineEmits<{
        (event: 'submit', orderLines: ReturnLine[]): void;
        (event: 'cancel'): void;
    }>();

    const orderLines = computed<Record<string, any>[]>(() => props.eligibility.orderLines ?? []);
    const reasonOptions = computed<Record<string, any>[]>(() => props.eligibility.reasonOptions ?? []);

    // A single-line order needs no picking: it is pre-selected, so the customer only has to give a
    // reason and a motivation.
    const single = computed(() => orderLines.value.length === 1);

    const selection = reactive<Record<number, {checked: boolean; reasonCode: number | null; motivation: string}>>(
        Object.fromEntries(orderLines.value.map((line) => [line.id, {checked: single.value, reasonCode: null, motivation: ''}]))
    );

    const incomplete = computed(() => {
        const checked = Object.values(selection).filter((line: {checked: any}) => line.checked);

        return (
            checked.length === 0 ||
            checked.some((line: {reasonCode: null; motivation: string}) => line.reasonCode === null || line.motivation.trim() === '')
        );
    });

    function submit(): void {
        emit(
            'submit',
            orderLines.value
                .filter((line) => selection[line.id].checked)
                .map((line) => ({
                    id: line.id,
                    reasonCode: selection[line.id].reasonCode,
                    motivation: selection[line.id].motivation.trim(),
                }))
        );
    }
</script>

<template>
    <div class="panel panel-default panel-body">
        <div
            v-for="line in orderLines"
            :key="line.id"
            class="return__entry">
            <div class="return__line">
                <label
                    v-if="!single"
                    class="return__pick">
                    <input
                        v-model="selection[line.id].checked"
                        type="checkbox"
                        :disabled="disabled" />
                    <span class="sr-only">{{ line.description || 'Artikel' }} retourneren</span>
                </label>

                <img
                    v-if="line.image"
                    class="return__image"
                    :src="line.image"
                    :alt="line.description || 'Artikel'" />

                <div class="chat-row__body">
                    <div class="return__title">{{ line.description || 'Artikel' }}</div>
                    <div class="return__meta">
                        <span>Bestelling #{{ eligibility.orderId }}</span>
                        <span v-if="line.price">· {{ toFixedLocal(Number(line.price)) }}</span>
                        <span v-if="line.quantity > 1">· {{ line.quantity }} stuks</span>
                    </div>
                    <div
                        v-if="line.articleNr"
                        class="return__sku text-muted">
                        {{ line.articleNr }}
                    </div>
                </div>

                <!--
                    The design puts a "Wijzig" link here to swap the article being returned. Every
                    returnable line of the order is already listed in this card (with a checkbox as
                    soon as there is more than one), so there is nothing behind such a link; it only
                    makes sense once the card shows a single AI-picked line instead of the full set.
                -->
            </div>

            <div
                v-if="selection[line.id].checked"
                class="return__fields">
                <div class="return__label">Waarom stuur je dit terug?</div>
                <div class="return__reason">
                    <ChatCheckmarkIcon v-if="selection[line.id].reasonCode !== null" />
                    <select
                        v-model="selection[line.id].reasonCode"
                        class="c-select"
                        :disabled="disabled"
                        :aria-label="`Retourreden voor ${line.description || 'artikel'}`">
                        <option :value="null">Kies een reden…</option>
                        <option
                            v-for="reason in reasonOptions"
                            :key="reason.code"
                            :value="reason.code">
                            {{ reason.label }}
                        </option>
                    </select>
                    <!--
                        The design shows this reason already filled in ("Past niet op mijn auto"),
                        picked by the assistant from what the customer wrote. Nothing in the turn
                        payload states a reason: ReturnTool flags the intent, the reason code comes
                        only from this control. Pre-selecting it needs Claude to name the reason
                        outright (a reasonCode on the return state, CLAUDE.md section 4.1) - never a
                        keyword match on the message here.
                    -->
                </div>

                <textarea
                    v-model="selection[line.id].motivation"
                    class="form-control return__motivation"
                    rows="3"
                    placeholder="Licht kort toe wat er aan de hand is… (verplicht)"
                    :disabled="disabled"></textarea>
            </div>
        </div>

        <!--
            The design contains a "Let op!" box with return conditions. The chat payload does not
            currently provide site-specific return-policy copy or a policy URL, so the box stays
            out of the customer UI until that content has a reliable source.
        -->

        <button
            type="button"
            class="c-button btn-block return__submit"
            :disabled="disabled || incomplete"
            @click="submit">
            Retour aanmelden
        </button>

        <div class="return__eligible text-muted">
            <ChatCheckmarkIcon
                variant="outline"
                :size="16" />
            <span>{{ single ? 'Dit artikel kun je nog retourneren' : 'Deze artikelen kun je nog retourneren' }}</span>
        </div>

        <button
            type="button"
            class="btn btn-link btn-block"
            :disabled="disabled"
            @click="emit('cancel')">
            Annuleren
        </button>
    </div>
</template>

<style scoped lang="scss">
    @import '../../sass/globals';
    .return__entry + .return__entry {
        margin-top: 14px;
        padding-top: 14px;
        border-top: 1px solid #e4e4e4;
    }

    .return__line {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .return__pick {
        flex: 0 0 auto;
        cursor: pointer;
    }

    .return__pick input {
        width: 18px;
        height: 18px;
        margin: 0;
        cursor: pointer;
    }

    .return__image {
        flex: 0 0 46px;
        width: 46px;
        height: 46px;
        object-fit: contain;
        border-radius: 4px;
        background: #ededeb;
    }

    .return__title {
        font-size: 15px;
        font-weight: 700;
        line-height: 1.3;
        color: $black;
    }

    .return__meta,
    .return__sku {
        margin-top: 1px;
        font-size: 13px;
    }

    .return__meta span + span {
        margin-left: 4px;
    }

    .return__fields {
        margin-top: 16px;
    }

    .return__label {
        margin-bottom: 7px;
        font-size: 14px;
        font-weight: 700;
        color: $black;
    }

    .return__reason {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .return__reason svg {
        flex-shrink: 0;
    }

    .return__motivation {
        margin-top: 10px;
    }

    .return__submit {
        margin-top: 16px;
    }

    .return__eligible {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 9px;
        margin-top: 10px;
        font-size: 13px;
    }

    .return__eligible svg {
        flex-shrink: 0;
    }
</style>
